import { MenuItemData, isMenuItemLink } from '../menu-item-data'
import { MutableRefObject, useEffect, useRef } from 'react'
import { getNextElement } from './get-next-element'
import { useMenuContext } from './use-menu-context'

type UseSubmenuProps = {
  item: MenuItemData
}

export const useSubmenu = ({
  item
}: UseSubmenuProps): [itemRefs: MutableRefObject<(HTMLLIElement | null)[]>] => {
  const { selected: currentSelection, showFocus, setShowFocus } = useMenuContext()
  const itemsRefs = useRef<Array<HTMLLIElement | null>>([])
  const selected = currentSelection === item
  const submenuItems = !isMenuItemLink(item) ? item.submenuItems : undefined

  useEffect(() => {
    itemsRefs.current = itemsRefs.current.slice(0, submenuItems?.length ?? 0)
  }, [submenuItems])

  useEffect(() => {
    if (showFocus && selected) {
      const current = itemsRefs.current[0]
      if (current) {
        current.focus()
      }
    } else if (!showFocus && selected) {
      const activeElement = document.activeElement
      const currentItem = itemsRefs.current.find(ref => {
        return ref === activeElement
      })
      if (currentItem) {
        currentItem.blur()
      }
    }
  }, [showFocus, selected])

  useEffect(() => {
    if (selected) {
      const eventListener = (event: KeyboardEvent): void => {
        const key = event.key
        switch (event.key) {
          case 'ArrowUp':
          case 'ArrowDown':
            setShowFocus(true)
            const nextElement = getNextElement(itemsRefs.current, event.key === 'ArrowUp')
            if (nextElement) {
              nextElement.focus()
            }
            event.preventDefault()
            break
          default:
            break
        }

        // Handle printable keys
        if (/[a-zA-Z0-9./<>?;:"'`!@#$%^&*()\\[\]{}_+=|\\-~,]/.test(key)) {
          const nextElement = itemsRefs.current.find(
            ref =>
              ref?.innerText?.toLowerCase().startsWith(key.toLowerCase()) ||
              ref?.textContent?.toLowerCase().startsWith(key.toLowerCase()) ||
              ref?.getAttribute('aria-label')?.toLowerCase().startsWith(key.toLowerCase())
          )

          if (nextElement) {
            nextElement.focus()
          }
        }
      }
      document.addEventListener('keydown', eventListener)
      return (): void => document.removeEventListener('keydown', eventListener)
    }
  }, [setShowFocus, selected])

  return [itemsRefs]
}
